/**
 * Logiciels connectés : le formulaire de toutes les connexions (« Autre
 * logiciel », un modèle tout prêt qui le préremplit, ou une connexion à
 * modifier). Il s'ouvre en trois champs (adresse avec des trous {numero}, clé,
 * quand l'utiliser) et les agents ; Callizy déduit les paramètres depuis
 * l'adresse. « Réglages avancés » déplie le formulaire complet, avec les mêmes
 * valeurs. Pendant l'appel, l'agent reçoit la réponse de l'API telle quelle ;
 * le serveur met lui-même le numéro de l'appelant. La clé n'est jamais
 * réaffichée. Les réglages techniques sont repliés.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, FlaskConical, X } from 'lucide-react'
import { toast } from 'sonner'
import type { AIAgent } from '@/api/aiAgents'
import {
  useSaveApiAction,
  useTestApiActionCall,
  useTestApiActionExample,
  type ApiAction,
  type ApiActionConfig,
  type ApiActionInput,
  type ApiActionModele,
  type ApiActionParam,
  type ApiActionTestResult,
} from '@/api/apiActions'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { AgentChecklist, Bloc, erreurServeur, Ligne, SignatureSection } from './parts'

const vide = (): ApiActionConfig => ({
  name: '',
  usage_hint: '',
  url: 'https://',
  method: 'GET',
  timeout_s: 5,
  auth_header: 'Authorization',
  signing_enabled: true,
  parameters: [{ name: 'telephone', source: 'numero_appelant', description: '' }],
  text_error: '',
  is_active: true,
})

export function CustomApiDialog({ companyId, agents, language, action, prerempli, agentDefaut, onClose }: {
  companyId: number | undefined; agents: AIAgent[]; language?: string; action: ApiAction | null
  /** Nouvelle connexion préremplie par un modèle tout prêt (lien d'un partenaire) */
  prerempli?: ApiActionModele | null
  /** Ouvert depuis la fiche d'un agent : cet agent est coché ; sinon tous */
  agentDefaut?: number
  onClose: () => void
}) {
  const { t } = useTranslation()
  const [cfg, setCfg] = useState<ApiActionConfig>(() => {
    if (!action) return prerempli ? { ...vide(), ...prerempli.config } : vide()
    const { id: _id, agent_ids: _a, modele: _m, slug: _s, has_auth: _h, auth_value_hint: _v, signing_secret_given: _sg, ...reste } = action
    return reste
  })
  // L'adresse de départ : tant qu'elle ne change pas, les paramètres réglés
  // (ceux de la connexion ou du modèle) sont gardés ; sinon le serveur les
  // redéduit de la nouvelle adresse.
  const urlDepart = action?.url ?? prerempli?.config.url
  // Nouvelle connexion hors fiche d'agent : tous les agents cochés. Tant que
  // la liste n'est pas touchée (null), elle suit les agents chargés.
  const [agentsChoisis, setAgentIds] = useState<number[] | null>(
    action ? action.agent_ids : agentDefaut ? [agentDefaut] : null)
  const agentIds = agentsChoisis ?? agents.map((a) => a.id)
  const [avance, setAvance] = useState(false)
  // Formulaire complet (« Réglages avancés ») ou les trois champs
  const [complet, setComplet] = useState(false)
  const [valeursEssai, setValeursEssai] = useState<Record<string, string>>({})
  const [resultatSimple, setResultatSimple] = useState<ApiActionTestResult | null>(null)
  const essaiBrouillon = useTestApiActionExample(companyId)
  const essaiEnregistre = useTestApiActionCall(companyId)
  const [cle, setCle] = useState<string | undefined>(undefined)
  const [actionId, setActionId] = useState<number | null>(action?.id ?? null)
  const [indiceCle, setIndiceCle] = useState(action?.auth_value_hint ?? '')
  const enregistrer = useSaveApiAction(companyId)

  const maj = <K extends keyof ApiActionConfig>(k: K, v: ApiActionConfig[K]) => setCfg((c) => ({ ...c, [k]: v }))
  const majParam = (i: number, p: Partial<ApiActionParam>) => maj('parameters', cfg.parameters.map((x, j) => (j === i ? { ...x, ...p } : x)))

  const sauver = async () => {
    const data: ApiActionInput = { ...cfg, agent_ids: agentIds }
    // Trois champs : les paramètres se déduisent de l'adresse côté serveur
    // (à la création, ou quand l'adresse a changé).
    if (!complet && cfg.url !== urlDepart) delete data.parameters
    if (cle !== undefined) data.auth_value = cle
    try {
      const r = await enregistrer.mutateAsync({ id: actionId ?? undefined, data })
      setActionId(r.id)
      setIndiceCle(r.auth_value_hint)
      setCle(undefined)
      onClose()
      toast.success(t('manager.connected.saved', 'Connexion enregistrée'))
    } catch (e) {
      toast.error(erreurServeur(e) || t('manager.apiAction.errSave', 'Impossible d’enregistrer'))
    }
  }

  /** Vers le formulaire complet : si l'adresse a changé, les paramètres déduits
   *  de la nouvelle adresse, comme le ferait le serveur, pour que les deux vues
   *  montrent les mêmes valeurs ; sinon ceux déjà réglés. */
  const basculerComplet = () => {
    if (!complet && cfg.url !== urlDepart) maj('parameters', parametresDeduits(cfg.url, cfg.parameters))
    setComplet((x) => !x)
  }

  /** Essai des trois champs : avant d'enregistrer, ou avec une nouvelle clé,
   *  la configuration en cours et la clé saisie (non stockée) ; sinon
   *  l'action enregistrée et sa clé. */
  const essayerSimple = async () => {
    const { parameters: _p, ...sansParams } = cfg
    try {
      const r = actionId && cle === undefined && action && cfg.url === action.url
        ? await essaiEnregistre.mutateAsync({ id: actionId, values: valeursEssai, language })
        : await essaiBrouillon.mutateAsync({ config: cfg.url === urlDepart ? cfg : sansParams, cle, values: valeursEssai, language })
      setResultatSimple(r)
    } catch (e) {
      toast.error(erreurServeur(e) || t('manager.apiAction.errTest', 'Essai impossible'))
    }
  }

  const sourceLabel = (s: ApiActionParam['source']) =>
    s === 'numero_appelant' ? t('manager.apiAction.sourceCaller', 'Numéro de l’appelant (mis par le serveur)')
      : s === 'numero_appelant_ou_donne' ? t('manager.apiAction.sourceCallerOrGiven', 'Numéro de l’appelant, ou celui qu’il donne')
      : t('manager.apiAction.sourceAsk', 'Demandé à l’appelant par l’IA')

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[680px] lg:max-w-3xl p-0 gap-0 max-h-[90vh] overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-200 dark:border-slate-800">
          <DialogTitle className="text-lg font-semibold">
            {action ? action.name : prerempli ? prerempli.config.name : t('manager.connected.otherTitle', 'Autre logiciel')}
          </DialogTitle>
        </DialogHeader>
        <div className="px-6 overflow-y-auto max-h-[calc(90vh-140px)]">

          {complet ? (
            <>
            <Bloc titre={t('manager.apiAction.blocWhat', 'L’action')}>
              <Ligne label={t('manager.apiAction.name', 'Nom')}>
                <Input value={cfg.name} onChange={(e) => maj('name', e.target.value)} placeholder={t('manager.apiAction.namePh', 'Où en est mon dossier')} />
              </Ligne>
              <Ligne label={t('manager.apiAction.usage', 'Quand l’utiliser')} aide={t('manager.apiAction.usageHelp', 'Phrase lue par l’IA pour décider.')}>
                <Textarea rows={2} value={cfg.usage_hint} onChange={(e) => maj('usage_hint', e.target.value)}
                  placeholder={t('manager.apiAction.usagePh', 'Quand l’appelant demande où en est son dossier ou sa commande.')} />
              </Ligne>
            </Bloc>

            <Bloc titre={t('manager.connected.blocAgents', 'Agents qui l’utilisent')}>
              <AgentChecklist agents={agents} value={agentIds} onChange={setAgentIds} />
            </Bloc>

            <Bloc titre={t('manager.apiAction.blocAddress', 'Adresse')}>
              <Ligne label={t('manager.apiAction.url', 'Adresse (https)')}
                aide={t('manager.apiAction.urlHelp', 'Mettez un paramètre entre accolades pour l’insérer dans l’adresse, par exemple {numero}. Pas dans le nom du site.')}>
                <Input value={cfg.url} onChange={(e) => maj('url', e.target.value)} placeholder="https://api.exemple.com/commandes/{numero}" />
              </Ligne>
              <Ligne label={t('manager.apiAction.authValue', 'Clé')}
                aide={indiceCle ? t('manager.apiAction.authKeep', 'Clé enregistrée : {{hint}}. Laissez vide pour la garder.', { hint: indiceCle }) : t('manager.apiAction.authHelp', 'Exemple : Bearer suivi de votre clé. Elle ne sera plus affichée.')}>
                <Input type="password" autoComplete="new-password" value={cle ?? ''} onChange={(e) => setCle(e.target.value)} placeholder={indiceCle || 'Bearer …'} />
              </Ligne>
              <button type="button" onClick={() => setAvance((x) => !x)}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700 dark:text-slate-200 hover:underline">
                <ChevronDown className={cn('h-3.5 w-3.5 text-slate-500 transition-transform', avance && 'rotate-180')} />
                {t('manager.connected.technical', 'Réglages techniques')}
              </button>
              {avance && (
                <div className="space-y-3 rounded-lg border border-slate-200 dark:border-slate-800 p-3">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <Ligne label={t('manager.apiAction.method', 'Méthode')}>
                      <select value={cfg.method} onChange={(e) => maj('method', e.target.value as 'GET' | 'POST')}
                        className="h-9 w-full rounded-md border border-input bg-transparent px-2 text-sm dark:bg-slate-900">
                        <option value="GET">GET</option>
                        <option value="POST">POST</option>
                      </select>
                    </Ligne>
                    <Ligne label={t('manager.apiAction.timeout', 'Délai (s)')}>
                      <Input type="number" min={1} max={10} value={cfg.timeout_s} onChange={(e) => maj('timeout_s', Number(e.target.value) || 5)} />
                    </Ligne>
                    <Ligne label={t('manager.apiAction.authHeader', 'Nom de l’en-tête')}>
                      <Input value={cfg.auth_header} onChange={(e) => maj('auth_header', e.target.value)} />
                    </Ligne>
                  </div>
                </div>
              )}
            </Bloc>

            <Bloc titre={t('manager.apiAction.blocParams', 'Paramètres envoyés')}
              aide={t('manager.apiAction.paramsHelp', 'Le numéro de l’appelant est mis par le serveur, jamais par l’IA.')}>
              {cfg.parameters.map((p, i) => (
                <div key={i} className="grid grid-cols-1 sm:grid-cols-[150px_1fr_auto] gap-2 items-start">
                  <Input value={p.name} onChange={(e) => majParam(i, { name: e.target.value })} placeholder={t('manager.apiAction.paramName', 'nom côté API')} />
                  <div className="space-y-2">
                    <select value={p.source} onChange={(e) => majParam(i, { source: e.target.value as ApiActionParam['source'] })}
                      className="h-9 w-full rounded-md border border-input bg-transparent px-2 text-sm dark:bg-slate-900">
                      <option value="numero_appelant">{sourceLabel('numero_appelant')}</option>
                      <option value="numero_appelant_ou_donne">{sourceLabel('numero_appelant_ou_donne')}</option>
                      <option value="demande">{sourceLabel('demande')}</option>
                    </select>
                    {p.source === 'numero_appelant_ou_donne' && (
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {t('manager.apiAction.sourceCallerOrGivenHelp', 'Le numéro d’où il appelle, sauf s’il en donne un autre (dit, ou tapé au clavier). Il pourra alors consulter ce qui est lié au numéro d’un proche.')}
                      </p>
                    )}
                    {p.source === 'demande' && (
                      <>
                        <Input value={p.description} onChange={(e) => majParam(i, { description: e.target.value })}
                          placeholder={t('manager.apiAction.paramDescPh', 'Le numéro de dossier, 9 caractères, à faire épeler')} />
                        <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                          <input type="checkbox" checked={p.required !== false} onChange={(e) => majParam(i, { required: e.target.checked })} />
                          {t('manager.apiAction.paramRequired', 'Obligatoire')}
                        </label>
                        {p.required === false && (
                          <p className="text-xs text-slate-500 dark:text-slate-400">{t('manager.apiAction.paramOptionalHelp', 'Demandé seulement si la première consultation ne trouve rien.')}</p>
                        )}
                      </>
                    )}
                  </div>
                  <Button type="button" variant="ghost" size="sm" className="h-9 w-9 p-0" onClick={() => maj('parameters', cfg.parameters.filter((_, j) => j !== i))}>
                    <X className="h-4 w-4 text-slate-500" />
                  </Button>
                </div>
              ))}
              <button type="button" className="text-xs font-medium text-slate-700 dark:text-slate-200 hover:underline"
                onClick={() => maj('parameters', [...cfg.parameters, { name: '', source: 'demande', description: '', required: true }])}>
                {t('manager.apiAction.addParam', 'Ajouter un paramètre')}
              </button>
            </Bloc>

            <Bloc titre={t('manager.apiAction.blocPhrases', 'Phrases')} aide={t('manager.apiAction.phrasesHelp', 'Vide = phrase par défaut.')}>
              <Ligne label={t('manager.apiAction.textError', 'API en panne ou trop lente')}>
                <Input value={cfg.text_error} onChange={(e) => maj('text_error', e.target.value)} placeholder="Je n’arrive pas à consulter le dossier pour le moment. Je prends vos coordonnées, l’équipe vous rappelle." />
              </Ligne>
            </Bloc>

            <Essayer companyId={companyId} cfg={cfg} actionId={actionId} language={language} />

            {actionId && action && (
              <SignatureSection companyId={companyId} connexionId={actionId} dejaDonne={action.signing_secret_given} />
            )}
            </>
          ) : (
            <FormulaireSimple cfg={cfg} maj={maj} cle={cle} setCle={setCle} indiceCle={indiceCle}
              agents={agents} agentIds={agentIds} setAgentIds={setAgentIds}
              valeursEssai={valeursEssai} setValeursEssai={setValeursEssai} resultat={resultatSimple} />
          )}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 px-6 py-4 border-t border-slate-200 dark:border-slate-800">
          <button type="button" onClick={basculerComplet}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700 dark:text-slate-200 hover:underline">
            <ChevronDown className={cn('h-4 w-4 text-slate-500 transition-transform', complet && 'rotate-180')} />
            {complet ? t('manager.connected.simpleView', 'Revenir à l’essentiel') : t('manager.connected.advanced', 'Réglages avancés')}
          </button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onClose}>{t('manager.apiAction.cancel', 'Annuler')}</Button>
            {!complet && (
              <Button type="button" variant="outline" className="gap-2" onClick={essayerSimple}
                disabled={!cfg.url.trim() || essaiBrouillon.isPending || essaiEnregistre.isPending}>
                <FlaskConical className="h-4 w-4" />
                {t('manager.connected.try', 'Essayer')}
              </Button>
            )}
            <Button type="button" onClick={sauver} disabled={enregistrer.isPending}>{t('manager.apiAction.save', 'Enregistrer')}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function Essayer({ companyId, cfg, actionId, language }: { companyId: number | undefined; cfg: ApiActionConfig; actionId: number | null; language?: string }) {
  const { t } = useTranslation()
  const [valeurs, setValeurs] = useState<Record<string, string>>({})
  const [resultat, setResultat] = useState<ApiActionTestResult | null>(null)
  const parAppel = useTestApiActionCall(companyId)
  const demandes = cfg.parameters.filter((p) => p.source === 'demande' && p.name)
  const setV = (k: string, v: string) => setValeurs((x) => ({ ...x, [k]: v }))

  const avecAppel = async () => {
    if (!actionId) return
    try {
      setResultat(await parAppel.mutateAsync({ id: actionId, values: valeurs, language }))
    } catch (e) {
      toast.error(erreurServeur(e) || t('manager.apiAction.errTest', 'Essai impossible'))
    }
  }

  return (
    <Bloc titre={t('manager.apiAction.blocTry', 'Essayer')}
      aide={t('manager.apiAction.tryHelp', 'Appelez l’adresse enregistrée avec des valeurs de test. Vous voyez ce que l’agent recevra.')}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Ligne label={t('manager.apiAction.tryNumber', 'Numéro de l’appelant')}>
          <Input value={valeurs.numero_appelant ?? ''} onChange={(e) => setV('numero_appelant', e.target.value)} placeholder="+15145550123" />
        </Ligne>
        {demandes.map((p) => (
          <Ligne key={p.name} label={(p.description || p.name) + (p.required === false ? ` (${t('manager.apiAction.optional', 'facultatif, vide = non envoyé')})` : '')}>
            <Input value={valeurs[p.name] ?? ''} onChange={(e) => setV(p.name, e.target.value)} />
          </Ligne>
        ))}
      </div>
      <Button type="button" variant="outline" size="sm" className="gap-2" onClick={avecAppel} disabled={!actionId || parAppel.isPending}>
        <FlaskConical className="h-4 w-4" />
        {t('manager.apiAction.tryCall', 'Appeler l’adresse enregistrée')}
      </Button>
      {resultat && <ReponseRecue resultat={resultat} />}
    </Bloc>
  )
}

/** Les trous {x} d'une adresse ; ceux du numéro de l'appelant sont mis par le serveur. */
const TROUS_NUMERO = ['telephone', 'numero_appelant', 'phone']
function trousDe(url: string): string[] {
  const vus: string[] = []
  for (const m of url.matchAll(/\{([A-Za-z0-9_]+)\}/g)) if (!vus.includes(m[1])) vus.push(m[1])
  return vus
}

/** Comme le serveur (consulter_api.deduire_parametres) : un paramètre par trou,
 *  le numéro de l'appelant pour {telephone}, {numero_appelant}, {phone}. Un
 *  paramètre déjà réglé garde ses réglages. */
function parametresDeduits(url: string, existants: ApiActionParam[]): ApiActionParam[] {
  return trousDe(url).map((x) => existants.find((p) => p.name === x) ?? (TROUS_NUMERO.includes(x.toLowerCase())
    ? { name: x, source: 'numero_appelant', description: '' }
    : { name: x, source: 'demande', description: x.replace(/[_-]+/g, ' '), required: true }))
}

function FormulaireSimple({ cfg, maj, cle, setCle, indiceCle, agents, agentIds, setAgentIds, valeursEssai, setValeursEssai, resultat }: {
  cfg: ApiActionConfig
  maj: <K extends keyof ApiActionConfig>(k: K, v: ApiActionConfig[K]) => void
  cle: string | undefined; setCle: (v: string) => void; indiceCle: string
  agents: AIAgent[]; agentIds: number[]; setAgentIds: (ids: number[]) => void
  valeursEssai: Record<string, string>; setValeursEssai: (v: Record<string, string>) => void
  resultat: ApiActionTestResult | null
}) {
  const { t } = useTranslation()
  const trous = trousDe(cfg.url).filter((x) => !TROUS_NUMERO.includes(x.toLowerCase()))
  const setV = (k: string, v: string) => setValeursEssai({ ...valeursEssai, [k]: v })

  return (
    <>
      <Bloc titre={t('manager.connected.simpleAddress', 'Adresse')}>
        <Input value={cfg.url} onChange={(e) => maj('url', e.target.value)} placeholder="https://api.monlogiciel.com/commandes/{numero}?tel={telephone}" />
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {t('manager.connected.simpleAddressHelp1', 'Mettez entre accolades ce que l’agent doit demander, par exemple {numero}.')}
          <br />
          {t('manager.connected.simpleAddressHelp2', '{telephone} = le numéro de l’appelant.')}
        </p>
      </Bloc>
      <Bloc titre={t('manager.apiAction.authValue', 'Clé')}>
        <Input type="password" autoComplete="new-password" value={cle ?? ''} onChange={(e) => setCle(e.target.value)} placeholder={indiceCle || 'sk_…'} />
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {indiceCle
            ? t('manager.apiAction.authKeep', 'Clé enregistrée : {{hint}}. Laissez vide pour la garder.', { hint: indiceCle })
            : t('manager.connected.simpleKeyHelp', 'La clé donnée par votre logiciel. Elle ne sera plus affichée.')}
        </p>
      </Bloc>
      <Bloc titre={t('manager.apiAction.usage', 'Quand l’utiliser')}>
        <Textarea rows={2} value={cfg.usage_hint} onChange={(e) => maj('usage_hint', e.target.value)}
          placeholder={t('manager.apiAction.usagePh', 'Quand l’appelant demande où en est son dossier ou sa commande.')} />
      </Bloc>
      <Bloc titre={t('manager.connected.blocAgents', 'Agents qui l’utilisent')}>
        <AgentChecklist agents={agents} value={agentIds} onChange={setAgentIds} />
      </Bloc>
      <Bloc titre={t('manager.connected.tryBox', 'Essayer')} aide={t('manager.connected.tryBoxHelp', 'Des valeurs de test, puis le bouton Essayer en bas.')}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Ligne label={t('manager.connected.testNumber', 'Numéro de téléphone d’un client')}>
            <Input value={valeursEssai.numero_appelant ?? ''} onChange={(e) => setV('numero_appelant', e.target.value)} placeholder="+15145550123" />
          </Ligne>
          {trous.map((x) => (
            <Ligne key={x} label={`{${x}}`}>
              <Input value={valeursEssai[x] ?? ''} onChange={(e) => setV(x, e.target.value)} />
            </Ligne>
          ))}
        </div>
        {resultat && <ReponseRecue resultat={resultat} />}
      </Bloc>
    </>
  )
}

/** Ce que l'agent recevra : la réponse de l'API telle quelle, rendue lisible
 *  (le JSON indenté), et la mention « tronquée » s'il le faut. */
export function ReponseRecue({ resultat }: { resultat: ApiActionTestResult }) {
  const { t } = useTranslation()
  let lisible = resultat.response ?? ''
  try {
    lisible = JSON.stringify(JSON.parse(lisible), null, 2)
  } catch {
    // Pas du JSON (texte, ou JSON tronqué) : montré tel quel.
  }
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-800 p-3 space-y-2 min-w-0">
      <p className="text-xs font-semibold text-slate-700 dark:text-slate-200">{t('manager.connected.agentReceives', 'Ce que l’agent recevra')}</p>
      <p className="text-xs text-slate-500 dark:text-slate-400 break-all">{t('manager.connected.calledUrl', 'Adresse appelée')} : {resultat.url}</p>
      {resultat.status !== 'found' ? (
        <>
          <p className="text-sm text-slate-700 dark:text-slate-200">{resultat.message}</p>
          {resultat.detail && <p className="text-xs text-slate-500 dark:text-slate-400">{t('manager.connected.detail', 'Détail')} : {resultat.detail}</p>}
        </>
      ) : (
        <pre className="text-xs bg-slate-50 dark:bg-slate-800/60 rounded p-2 max-h-80 overflow-auto whitespace-pre-wrap break-all">{lisible}</pre>
      )}
      {resultat.truncated && <p className="text-xs text-slate-500 dark:text-slate-400">{t('manager.connected.truncated', 'Réponse tronquée : plus de 20 000 caractères.')}</p>}
    </div>
  )
}
